/**
 * ForecastEdgeAITab — the ForecastEDGE AI tab for dual-access users
 * (HasExecutiveAccess === 2 with HasAgenticAccess === 1). An
 * Analyst/Executive toggle swaps between the analyst (AgenticV2) and
 * executive surfaces in place; the route stays /agentic-v2 throughout,
 * so header tab highlighting is unaffected.
 *
 * Layout (2026-08-22 UI review, marks 1–2; analyst placement revised
 * 2026-08-23): no full-width strip. The old flexShrink:0 toggle row
 * consumed a band above both surfaces, which (a) stopped each surface's
 * side panel short of the tab's top and (b) pinned the toggle to the
 * viewport's right edge, unrelated to any content edge. Surfaces fill
 * the full tab height (side panels run to the top), and each surface
 * anchors the toggle to its own content header:
 *
 * - Executive: an absolute pointer-transparent overlay right-aligned to
 *   the executive content container's right edge (replicating sidebar
 *   offset + EXEC_CONTENT_CONTAINER_SX geometry). Height 52 with
 *   centered content = the exact geometry of WhatIfView's nav band, so
 *   the toggle and the back affordance share a vertical centerline.
 * - Analyst: injected into AgenticV2's header row via the surfaceToggle
 *   prop, rendered title-adjacent ("ForecastEDGE AI [Analyst|Executive]
 *   … New Chat"). The toggle is a surface-mode control, so it rides
 *   with the surface's name — deliberately apart from the session
 *   controls on the row's right — and inherits the centered content
 *   container's alignment at any panel width. An overlay can't do this:
 *   the analyst left panel is user-resizable, so no fixed container
 *   geometry exists to mirror.
 *
 * Mount policy: the analyst surface stays mounted (hidden) across
 * toggles so an in-flight chat session survives; the executive surface
 * mounts lazily on first use (avoids fetching executive options for
 * users who never flip) and then also stays mounted to preserve
 * brand/scenario selection.
 *
 * Executive-only users (HasExecutiveAccess === 1) never see this
 * component — RouteParams gives them ExecutiveView directly at
 * /executive.
 */
import React, { useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';

import AgenticV2 from '../AgenticV2';
import ExecutiveView from '../ExecutiveView';
import { EXEC_SIDEBAR_WIDTH } from '../ExecutiveView/Sidebar';
import { EXEC_CONTENT_CONTAINER_SX } from '../ExecutiveView/layout';

type SurfaceView = 'analyst' | 'executive';

const ForecastEdgeAITab: React.FC = () => {
  const [view, setView] = useState<SurfaceView>('analyst');
  const [executiveMounted, setExecutiveMounted] = useState(false);

  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: SurfaceView | null
  ) => {
    // Clicking the already-selected button yields null — keep current.
    if (next === null) return;
    if (next === 'executive') setExecutiveMounted(true);
    setView(next);
  };

  const toggle = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={view}
      onChange={handleViewChange}
      aria-label="ForecastEDGE AI surface"
      // pointerEvents:auto opts back in from the executive overlay's
      // pointer-transparent wrapper; inert in the analyst header row.
      sx={{ pointerEvents: 'auto', bgcolor: 'background.paper' }}
    >
      <ToggleButton value="analyst" sx={{ textTransform: 'none', px: 2 }}>
        Analyst
      </ToggleButton>
      <ToggleButton value="executive" sx={{ textTransform: 'none', px: 2 }}>
        Executive
      </ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <Box
      sx={{
        height: 'calc(100vh - 120px)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: view === 'analyst' ? 'block' : 'none',
        }}
      >
        <AgenticV2 embedded surfaceToggle={toggle} />
      </Box>
      {executiveMounted && (
        <Box
          sx={{
            height: '100%',
            display: view === 'executive' ? 'block' : 'none',
          }}
        >
          <ExecutiveView embedded active={view === 'executive'} />
        </Box>
      )}

      {view === 'executive' && (
        // Right-aligned to the executive content container: the overlay
        // strip starts at the sidebar's right edge and re-applies the
        // shared container geometry, so the toggle's right edge lands
        // exactly on the container's content edge (mark 2). Height 52
        // with centered content = the exact geometry of WhatIfView's
        // nav band (2026-08-22 mark 5), so the toggle and the back
        // affordance share a vertical centerline. (The analyst surface
        // needs no overlay — its toggle renders inside AgenticV2's
        // header row via surfaceToggle.)
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: EXEC_SIDEBAR_WIDTH,
            right: 0,
            height: 52,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              ...EXEC_CONTENT_CONTAINER_SX,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {toggle}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ForecastEdgeAITab;
